import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { ArrowLeft, Send, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import useReducedMotion from '../hooks/useReducedMotion';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function ForgotPasswordPage() {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const reduced = useReducedMotion();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError(t('auth.duo.forgot.emailRequired'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(API + '/auth/forgot-password', { email: email.trim() });
      setSent(true);
      toast.success(t('auth.duo.forgot.sentTitle'));
    } catch (err) {
      const msg = getApiErrorMessage(err, lang);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center duo-bg px-4 py-10" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <motion.div
          className="text-center mb-8"
          initial={reduced ? false : { opacity: 0, y: -10 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--duo-green)' }}>
              <span className="text-white font-duo-display text-2xl leading-none">K</span>
            </div>
            <span className="font-duo-display text-2xl text-[var(--duo-text)]">Kariyer Online</span>
          </Link>
        </motion.div>

        <motion.div
          className="bg-white rounded-2xl p-8"
          initial={reduced ? false : { opacity: 0, scale: 0.96 }}
          animate={reduced ? undefined : { opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          style={{ border: '2px solid var(--duo-stroke)' }}
        >
          {sent ? (
            <div className="text-center" data-testid="forgot-sent-state">
              <motion.div
                className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
                style={{ backgroundColor: 'var(--duo-green)', boxShadow: '0 4px 0 0 var(--duo-green-dark)' }}
                initial={reduced ? false : { scale: 0.5, opacity: 0 }}
                animate={reduced ? undefined : { scale: [0.5, 1.2, 0.95, 1], opacity: 1 }}
                transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.5, 0.8, 1] }}
              >
                <CheckCircle2 size={32} strokeWidth={2.5} className="text-white" />
              </motion.div>
              <h2 className="font-duo-display text-2xl text-[var(--duo-text)] mb-3">
                {t('auth.duo.forgot.sentTitle')}
              </h2>
              <p className="text-sm text-[var(--duo-text-soft)] font-medium mb-7">
                {t('auth.duo.forgot.sentDesc').replace('{email}', email)}
              </p>
              <Link to="/login" className="btn-duo-secondary inline-flex" data-testid="back-to-login-link">
                <ArrowLeft size={16} />
                {t('auth.duo.forgot.backToLogin')}
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-duo-display text-3xl text-center text-[var(--duo-text)] mb-2" data-testid="forgot-title">
                {t('auth.duo.forgot.title')}
              </h2>
              <p className="text-sm text-[var(--duo-text-soft)] font-medium mb-7">
                {t('auth.duo.forgot.subtitle')}
              </p>

              {error && (
                <div className="bg-[#FFF5F5] border-2 border-[var(--duo-error)] text-[var(--duo-error-dark)] px-4 py-2 rounded-xl mb-4 text-sm font-bold" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[var(--duo-text-soft)] mb-2">
                    {t('auth.duo.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={t('auth.duo.emailPlaceholder')}
                    className="input-duo"
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
                <button type="submit" disabled={loading} className="btn-duo-primary w-full" data-testid="forgot-submit-btn">
                  <Send size={16} />
                  {loading ? t('auth.duo.forgot.sending') : t('auth.duo.forgot.cta')}
                </button>
              </form>

              <p className="text-center mt-6 text-sm text-[var(--duo-text-soft)] font-medium">
                {t('auth.duo.forgot.rememberedQ')}{' '}
                <Link to="/login" className="text-[var(--duo-blue)] font-bold uppercase tracking-wider text-xs hover:underline" data-testid="login-link">
                  {t('auth.duo.loginLink')}
                </Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
